import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Alert,
  CircularProgress,
  Stack,
  Fab,
} from '@mui/material';
import {
  Pets as PetsIcon,
  Add as AddIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
  Cake as CakeIcon,
  MonitorWeight as WeightIcon,
} from '@mui/icons-material';
import { animalService, Animal } from '../../services/animalService';

interface MyAnimalsProps {
  onAddAnimal?: () => void;
  onViewDetail?: (animal: Animal) => void;
  onEditAnimal?: (animal: Animal) => void;
}

export const MyAnimals: React.FC<MyAnimalsProps> = ({ onAddAnimal, onViewDetail, onEditAnimal }) => {
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const fetchAnimals = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await animalService.getMyAnimals();
      setAnimals(response.results);
    } catch (err: any) {
      console.error('Erreur lors du chargement des animaux:', err);
      setError('Erreur lors du chargement de vos animaux');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnimals();
  }, []);

  const calculateAge = (dateNaissance: string) => {
    if (!dateNaissance) return 'Âge inconnu';
    
    const birthDate = new Date(dateNaissance);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    if (age === 0) {
      const months = today.getMonth() - birthDate.getMonth() + 
        (12 * (today.getFullYear() - birthDate.getFullYear()));
      return months > 0 ? `${months} mois` : 'Nouveau-né';
    }
    
    return `${age} an${age > 1 ? 's' : ''}`;
  };

  const getSpecieColor = (espece: string) => {
    const colors: Record<string, string> = {
      'Chien': '#FF9800',
      'Chat': '#9C27B0',
      'Lapin': '#4CAF50',
      'Oiseau': '#2196F3',
      'Poisson': '#00BCD4',
      'Reptile': '#795548',
      'Hamster': '#FF5722',
      'Furet': '#607D8B',
      'Cochon d\'Inde': '#FFC107',
    };
    return colors[espece] || '#757575';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" action={
          <Button color="inherit" onClick={fetchAnimals}>
            Réessayer
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PetsIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Mes animaux
          </Typography>
        </Box>
        
        {onAddAnimal && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAddAnimal}
            size="large"
          >
            Ajouter un animal
          </Button>
        )}
      </Box>

      {animals.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <PetsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Vous n'avez pas encore d'animaux enregistrés
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Commencez par ajouter votre premier compagnon !
          </Typography>
          {onAddAnimal && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onAddAnimal}
              size="large"
            >
              Ajouter mon premier animal
            </Button>
          )}
        </Paper>
      ) : (
        <Stack spacing={2}>
          {animals.map((animal) => (
            <Card key={animal.id} sx={{ 
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 4,
              }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  {/* Avatar de l'animal */}
                  <Avatar
                    sx={{ 
                      width: 80, 
                      height: 80, 
                      bgcolor: getSpecieColor(animal.espece),
                      fontSize: '2rem'
                    }}
                  >
                    {animal.photo_url ? (
                      <img 
                        src={animal.photo_url} 
                        alt={animal.nom}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <PetsIcon fontSize="large" />
                    )}
                  </Avatar>

                  {/* Informations principales */}
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="h5" component="h2">
                        {animal.nom}
                      </Typography>
                      <Chip 
                        label={animal.espece}
                        size="small"
                        sx={{ 
                          bgcolor: getSpecieColor(animal.espece),
                          color: 'white'
                        }}
                      />
                      {animal.sexe === 'M' ? (
                        <MaleIcon color="primary" />
                      ) : (
                        <FemaleIcon sx={{ color: 'pink' }} />
                      )}
                    </Box>

                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                      {animal.race || 'Race non spécifiée'}
                    </Typography>

                    {/* Détails sur une ligne */}
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                      {animal.date_naissance && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CakeIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {calculateAge(animal.date_naissance)}
                          </Typography>
                        </Box>
                      )}

                      {animal.poids && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <WeightIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {animal.poids} kg
                          </Typography>
                        </Box>
                      )}

                      {animal.sterilise && (
                        <Chip 
                          label="Stérilisé(e)" 
                          size="small" 
                          color="success" 
                          variant="outlined"
                        />
                      )}

                      {animal.numero_puce && (
                        <Chip 
                          label="Pucé" 
                          size="small" 
                          color="info" 
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Actions */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => onViewDetail?.(animal)}
                    >
                      Voir détails
                    </Button>
                    {onEditAnimal && (
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => onEditAnimal(animal)}
                      >
                        Modifier
                      </Button>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Bouton flottant pour ajouter un animal (sur mobile) */}
      {onAddAnimal && animals.length > 0 && (
        <Fab
          color="primary"
          aria-label="ajouter animal"
          onClick={onAddAnimal}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            display: { xs: 'flex', sm: 'none' }
          }}
        >
          <AddIcon />
        </Fab>
      )}
    </Box>
  );
}; 